import React, { FC, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import Svg from "@natera/material/lib/svg";

import { routes } from "@app/routing";
import SettigsSVG from "@app/postBill/assets/svg/settings.svg";
import CreditCardSVG from "@app/postBill/assets/svg/creditCard.svg";
import MagnifierIcon from "@app/postBill/assets/svg/magnifier-icon.svg";
import { getFormattedLocalizeDate } from "@app/utils";
import { convertBalance, maskedPaymentMethod } from "@app/postBill/utils";
import { PaymentMethod, BillingPaymentPlanStatus } from "@app/postBill/models";
import { IntlContext, PostBillContext } from "@app/provider";
import { usePostBillHeapEvent } from "@app/postBill/hooks";
import StatusChip from "@app/postBill/components/statusChip";
import { HEAP_EVENTS } from "@app/provider/types";

import "../billingStatementCard/billingStatementCard.scss";

interface BillingPaymentPlanCardProps {
  currency: string;
  dueDate: string;
  caseNumber: string;
  remainingBalance: number;
  paymentMethod?: PaymentMethod;
  installmentAmount: number;
  numInstallments: number;
  currentInstallmentIndex: number;
  paymentPlanId: string;
  status: BillingPaymentPlanStatus;
}

const messages = defineMessages({
  billingPaymentPlanCardTitle: {
    id: "billingPaymentPlanCardTitle",
    defaultMessage:
      "Autopay (payment {currentInstallmentIndex} of {numInstallments})",
  },
  billingPaymentPlanCardDueDateTitle: {
    id: "billingPaymentPlanCardDueDateTitle",
    defaultMessage: "Due date",
  },
  billingPaymentPlanCardCaseNumberTitle: {
    id: "billingPaymentPlanCardCaseNumberTitle",
    defaultMessage: "Case number",
  },
  billingPaymentPlanCardRemainingBalanceTitle: {
    id: "billingPaymentPlanCardRemainingBalanceTitle",
    defaultMessage: "Remaining balance",
  },
  billingPaymentPlanCardPaymentMethodTitle: {
    id: "billingPaymentPlanCardPaymentMethodTitle",
    defaultMessage: "Payment method",
  },
  billingPaymentPlanCardButtonManagePaymentPlan: {
    id: "billingPaymentPlanCardButtonManagePaymentPlan",
    defaultMessage: "Manage payment plan",
  },
  billingPaymentPlanCardButtonPayFullBalance: {
    id: "billingPaymentPlanCardButtonPayFullBalance",
    defaultMessage: "Pay full balance",
  },
  billingPaymentPlanViewDetails: {
    id: "billingPaymentPlanViewDetails",
    defaultMessage: "View details",
  },
});

const BillingPaymentPlanCard: FC<BillingPaymentPlanCardProps> = ({
  currency,
  dueDate,
  caseNumber,
  remainingBalance,
  paymentMethod,
  installmentAmount,
  numInstallments,
  currentInstallmentIndex,
  paymentPlanId,
  status,
}) => {
  const intl = useIntl();
  const { currentLanguage } = useContext(IntlContext);
  const { setPageFrom } = useContext(PostBillContext);
  const navigate = useNavigate();
  const { trackPostBillHeapEvent } = usePostBillHeapEvent();

  const handleManagePaymentPlanButton = () => {
    trackPostBillHeapEvent({
      eventKey: HEAP_EVENTS.upp_click_billing_manage_payment_plan,
    });
    setPageFrom(routes.billingCenterPaymentPlanHistory);
    navigate(routes.billingPaymentPlanDetail, {
      state: {
        paymentPlanId,
      },
    });
  };

  const handlePayFullBalance = () => {
    trackPostBillHeapEvent({
      eventKey: HEAP_EVENTS.upp_click_billing_payment_plan_pay_full_balance,
    });
    navigate(routes.billingPayment, {
      state: {
        paymentPlanId,
        payFullBalance: true,
        from: routes.billingCenterPaymentPlanHistory,
      },
    });
  };

  return (
    <div className="billing-history-card" data-testid="payment-plan-card">
      <div className="billing-history-card__display">
        <div className="billing-history-card__header">
          <div className="billing-history-card__title">
            {intl.formatMessage(messages.billingPaymentPlanCardTitle, {
              currentInstallmentIndex,
              numInstallments,
            })}
          </div>
          <div className="billing-history-card__amount">
            {convertBalance(installmentAmount, true, currency)}
          </div>
        </div>
        <div
          className="billing-history-card__status"
          data-testid="paymentPlanStatus"
        >
          <StatusChip status={status} />
        </div>
        <div className="billing-history-card__content">
          <div className="billing-history-card__row">
            <span className="billing-history-card__label">
              {intl.formatMessage(messages.billingPaymentPlanCardDueDateTitle)}
            </span>
            <span className="billing-history-card__value">
              {getFormattedLocalizeDate(dueDate, currentLanguage)}
            </span>
          </div>
          <div className="billing-history-card__row">
            <span className="billing-history-card__label">
              {intl.formatMessage(
                messages.billingPaymentPlanCardCaseNumberTitle
              )}
            </span>
            <span className="billing-history-card__value">{caseNumber}</span>
          </div>
          <div className="billing-history-card__row">
            <span className="billing-history-card__label">
              {intl.formatMessage(
                messages.billingPaymentPlanCardRemainingBalanceTitle
              )}
            </span>
            <span className="billing-history-card__value">
              {convertBalance(remainingBalance, false, currency)}
            </span>
          </div>
          {paymentMethod && (
            <div className="billing-history-card__row">
              <span className="billing-history-card__label">
                {intl.formatMessage(
                  messages.billingPaymentPlanCardPaymentMethodTitle
                )}
              </span>
              <span className="billing-history-card__value">
                {maskedPaymentMethod(paymentMethod)}
              </span>
            </div>
          )}
        </div>
        <div className="billing-history-card__buttons">
          {(status === BillingPaymentPlanStatus.COMPLETED ||
            status === BillingPaymentPlanStatus.CANCELLED) && (
            <button className="button" onClick={handleManagePaymentPlanButton}>
              <Svg content={MagnifierIcon} className="icon" />
              {intl.formatMessage(messages.billingPaymentPlanViewDetails)}
            </button>
          )}
          {(status === BillingPaymentPlanStatus.ACTIVE ||
            status === BillingPaymentPlanStatus.ERROR) && (
            <>
              <button
                className="button"
                onClick={handleManagePaymentPlanButton}
              >
                <Svg content={SettigsSVG} className="icon" />
                {intl.formatMessage(
                  messages.billingPaymentPlanCardButtonManagePaymentPlan
                )}
              </button>
              <button className="button" onClick={handlePayFullBalance}>
                <Svg content={CreditCardSVG} className="icon" />
                {intl.formatMessage(
                  messages.billingPaymentPlanCardButtonPayFullBalance
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BillingPaymentPlanCard;
